import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../firebase';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import AeroSidebar from '../components/AeroSidebar/AeroSidebar';
import GlassProvider from '../components/Aero/GlassProvider';
import GlassButton from '../components/Aero/GlassButton';
import AeroDotLoader from '../components/Aero/AeroDotLoader';
import useAeroShellData from './useAeroShellData';
import { UserTestingSkeleton } from '../components/Skeletons/Skeletons';
import AeroAuth from '../components/AeroAuth/AeroAuth';
import AeroPricingModal from '../components/Aero/AeroPricingModal';
import { computeSiteAccess } from '../utils/siteAccess';
import FeedbackModal from '../components/FeedbackModal/FeedbackModal';
import { isAeroDomain } from '../utils/domainUtils';
import { getPlanKeyFromPriceId } from '../utils/pricingUtils';
import '../components/AeroSidebar/AeroSidebar.css';
import '../components/Aero/aero-components.css';
import '../pages/UserTesting/UserTestingLayout.css';
import '../components/AeroAuth/AeroAuth.css';
import '../styles/aero-v3-overlays.css';

/**
 * AeroLayout — persistent shell for all Aero product pages.
 *
 * Handles ONCE (not per-page):
 *   1. Auth guard — redirects to /auth if not authenticated
 *   2. Subscription fetch — shared across User Testing, Responsive, Iterate
 *   3. Sidebar state — collapsed/expanded, mobile toggle
 *   4. GlassProvider — glass engine context for child components
 *
 * Child pages receive { user, subscriptionType, planKey, subscriptionStatus }
 * via React Router's outlet context (useOutletContext).
 *
 * Navigation between User Testing / Responsive / Iterate is INSTANT because
 * this component never unmounts — only the <Outlet> content swaps.
 */
function AeroLayout() {
  const user = useAuth();
  const { pathname } = useLocation();
  // Sidebar counts, recent tests, the plan meter and the account (v3 shell).
  // Also handed to pages through the outlet context (Home reads the sites).
  const shellData = useAeroShellData(user?.uid, pathname);

  const [usualCollapsed, setUsualCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('aero_sidebar_collapsed');
      if (stored !== null) return stored === 'true';
    } catch {}
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 1024;
    }
    return false;
  });

  // Session review is dark and the sidebar has no dark look yet, so (as a
  // stopgap, docs/TODO.md) the sidebar folds to the icon rail while a review
  // is open. One click opens it; that choice is kept for reviews in this
  // browser ('aero_sidebar_collapsed_review'), and leaving review restores
  // the usual state ('aero_sidebar_collapsed'), which review never changes.
  const inReview = /^\/user-testing\/[^/]+\/sessions\/[^/]+/.test(pathname);
  const [reviewCollapsed, setReviewCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('aero_sidebar_collapsed_review');
      if (stored !== null) return stored === 'true';
    } catch {}
    return true;
  });

  const sidebarCollapsed = inReview ? reviewCollapsed : usualCollapsed;

  const setSidebarCollapsed = (value) => {
    const key = inReview ? 'aero_sidebar_collapsed_review' : 'aero_sidebar_collapsed';
    const set = inReview ? setReviewCollapsed : setUsualCollapsed;
    set((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      try { localStorage.setItem(key, String(next)); } catch {}
      return next;
    });
  };

  const [subscriptionType, setSubscriptionType] = useState('regular');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  // Inputs for the free-plan access rule (utils/siteAccess.js); null until read.
  const [accessInputs, setAccessInputs] = useState(null);
  // Profile admin flag, read once here (same read pattern AdminDashboard uses)
  // and threaded down through the outlet context so the /users page can gate
  // on it and the sidebar can show the admin nav item. Resolved BEFORE loading
  // flips to false, so children never mount with a stale value.
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);

  // Upgrade modal mounted at the shell so gated features anywhere under Aero
  // can open it IN PLACE (via the openUpgradeModal outlet-context callback)
  // instead of navigating to /account. planId/trialConsume are read from the
  // same subscription doc so the modal labels the current plan correctly.
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [planId, setPlanId] = useState(null);
  const [trialConsume, setTrialConsume] = useState(null);
  const openUpgradeModal = useCallback(() => setShowUpgradeModal(true), []);

  useEffect(() => {
    if (!user) return; // Auth still resolving (undefined) or not logged in (null)

    async function fetchSubscription() {
      try {
        const db = firebase.firestore();

        // Admin gate: a doc in user/{uid}/profile with isAdmin === true (or the
        // legacy string "true"), exactly like AdminDashboard. Best-effort: a
        // non-admin's profile read may be denied, which simply leaves isAdmin
        // false. Read alongside the subscription so both settle before loading
        // flips off.
        try {
          const profileSnap = await db
            .collection('user')
            .doc(user.uid)
            .collection('profile')
            .get();
          const admin = profileSnap.docs.some((d) => {
            const v = d.data()?.isAdmin;
            return v === true || v === 'true';
          });
          setIsAdmin(admin);
        } catch (profileErr) {
          console.error('Error fetching admin profile:', profileErr);
        }

        const subscriptionSnapshot = await db
          .collection('user')
          .doc(user.uid)
          .collection('subscriptions')
          .orderBy('created', 'desc')
          .limit(1)
          .get();

        if (!subscriptionSnapshot.empty) {
          const sub = subscriptionSnapshot.docs[0].data();
          setSubscriptionStatus(sub.status || null);
          // Same derivation Account.js uses for the pricing modal labels.
          const priceId = sub.items?.[0]?.plan?.id || sub.items?.[0]?.price?.id;
          // Tier by price-id MEMBERSHIP (getPlanKeyFromPriceId), never by
          // substring: real Stripe price ids are random (price_1R16...), and
          // the subscription doc has no top-level plan string, so the old
          // sub.plan.includes('pro'|'solo'|'basic') resolved every paying
          // user to 'regular'. Only a live subscription (active, trialing or
          // past_due, as everywhere else) grants a paid tier, matching
          // api/ut-session.js. A card that failed once keeps its plan while
          // Stripe retries it.
          if (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due') {
            const planKeyFromPrice = getPlanKeyFromPriceId(priceId);
            if (planKeyFromPrice) {
              setSubscriptionType(planKeyFromPrice);
            }
          }
          setPlanId(priceId || null);
          setTrialConsume('true');
        }

        // Admin plan override wins over the real subscription as REAL
        // entitlement (drives the sidebar session meter + every feature gate).
        // Read off the user doc the live user can already access. Keys map to
        // the sidebar's tier terms ('regular' is its word for free); an
        // override (including a 'free' downgrade) always takes precedence.
        try {
          const userDoc = await db.collection('user').doc(user.uid).get();
          const userData = userDoc.exists ? userDoc.data() : {};
          const override = userData.planOverride || null;
          // What the site-serving gate reads (api/_lib/siteGate.js), so the
          // Sites pages can say when a Free site is offline. A paid override
          // counts, as it does for visitors.
          let hasPayment = false;
          try {
            const paySnap = await db.collection('user').doc(user.uid).collection('payments').limit(1).get();
            hasPayment = !paySnap.empty;
          } catch (payErr) {
            console.error('Error reading payments:', payErr);
          }
          setAccessInputs({
            hasPayment,
            planOverride: userData.planOverride || null,
            trialUsed: userData.trialUsed === true,
            trialStart: userData.firstProjectDate || userData.signUpDate || null,
          });
          if (override && typeof override.plan === 'string') {
            const OVERRIDE_TO_TIER = { free: 'regular', basic: 'basic', solo: 'solo', pro: 'pro' };
            const tier = OVERRIDE_TO_TIER[override.plan.toLowerCase()];
            if (tier) setSubscriptionType(tier);
          }
        } catch (overrideErr) {
          console.error('Error reading plan override:', overrideErr);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchSubscription();
  }, [user]);

  // 'regular' is the sidebar's term for free users; child pages may need 'free'.
  // Computed unconditionally so useMemo below is always called at the same position.
  const planKey = subscriptionType === 'regular' ? 'free' : subscriptionType;
  // 'paid' | 'trial' | 'expired' for this user's sites, null while unread (the
  // pages then show nothing new rather than a wrong warning).
  const siteAccess = useMemo(
    () => (accessInputs ? computeSiteAccess({ subscriptionStatus, ...accessInputs }) : null),
    [accessInputs, subscriptionStatus]
  );

  // Memoize the context object so child pages don't re-render on every
  // AeroLayout render unless subscription data actually changes.
  // Must be called unconditionally (before any early returns) per Rules of Hooks.
  // The account falls back to the signed-in email until the user doc is read.
  const shell = useMemo(
    () => ({
      ...shellData,
      account: {
        name: shellData.account.name,
        email: shellData.account.email || user?.email || '',
      },
    }),
    [shellData, user]
  );

  const outletContext = useMemo(
    () => ({ user, subscriptionType, planKey, subscriptionStatus, siteAccess, isAdmin, openUpgradeModal, shell }),
    [user, subscriptionType, planKey, subscriptionStatus, siteAccess, isAdmin, openUpgradeModal, shell]
  );

  // Auth still resolving: the logo's blue dot as the loader (v3), instead of a
  // skeleton, so signed-out visitors never see a flash of dashboard chrome.
  if (user === undefined) {
    return (
      <div className="aero-loading">
        <AeroDotLoader size="lg" label="Loading" />
      </div>
    );
  }

  // Not authenticated — on Aero domain render inline auth; elsewhere redirect.
  if (user === null) {
    if (isAeroDomain()) {
      return <AeroAuth />;
    }
    const redirectPath = window.location.pathname + window.location.search;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(redirectPath)}`} />;
  }

  // Authenticated, subscription still loading.
  if (loading) {
    return <UserTestingSkeleton />;
  }

  return (
    <GlassProvider scanRadius={320}>
      <div className={`ut-layout ut-base aero-page ut-theme ${sidebarCollapsed ? 'ut-sidebar-is-collapsed' : ''}`}>

        {/* Mobile hamburger — only visible when sidebar is hidden */}
        {sidebarCollapsed && (
          <button
            className="ut-mobile-menu-toggle"
            onClick={() => setSidebarCollapsed(false)}
            aria-label="Open menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}

        {/* Backdrop overlay on mobile when sidebar is open */}
        {!sidebarCollapsed && (
          <div
            className="ut-sidebar-backdrop"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        {/*
          AeroSidebar is rendered HERE — outside the Outlet — so it NEVER
          unmounts when navigating between User Testing, Responsive, Iterate.
          The logo stays still, usage bar stays still, active nav item updates
          via useLocation() inside AeroSidebar.
        */}
        <AeroSidebar
          shell={shell}
          subscriptionType={subscriptionType}
          isAdmin={isAdmin}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          onNavClose={() => setSidebarCollapsed(true)}
        />

        {/* Only the content panel swaps during navigation */}
        <main className="ut-main">
          <Outlet context={outletContext} />
        </main>
      </div>

      {/* Floating feedback button — portalled to <body> so it never
          becomes a child of any flex/grid container in the layout tree.
          Visible on every Aero page. */}
      {ReactDOM.createPortal(
        <GlassButton
          fill="glass"
          size="sm"
          className="aero-feedback-fab ut-theme"
          onClick={() => setShowFeedback(true)}
          aria-label="Send Feedback"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Feedback
        </GlassButton>,
        document.body
      )}

      <FeedbackModal show={showFeedback} onHide={() => setShowFeedback(false)} />

      {/* Upgrade modal opened in place by gated features anywhere under Aero. */}
      <AeroPricingModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        trialConsume={trialConsume}
        planId={planId}
      />
    </GlassProvider>
  );
}

export default AeroLayout;
